import classnames from 'classnames';
import debounce from 'lodash/debounce';
import React, { useState } from 'react';
import { Col, Container, Form, Row, Table } from 'react-bootstrap';
import { BsSearch } from 'react-icons/bs';
import { useQuery } from 'react-query';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';
import BackgroundImages from 'v2/common/Background/BackgroundImages';
import LoadingIndicator from 'v2/common/LoadingIndicator/LoadingIndicator';
import { StrapiApiError, ReadOnlyUser } from 'v2/models';
import { infiniteStrapiUsersKeys } from 'v2/queries';
import restFactory from 'v2/utils/restFactory';
import { getUserRole, getUserRoleStr } from 'v2/utils/user';
import { ghostUser } from '../../../actions';
import styles from './users.module.scss';

interface UsersProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  ghostUser: Function;
}

const Users = ({ ghostUser }: UsersProps) => {
  const history = useHistory();
  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');

  const debouncedSearchUpdate = React.useCallback(
    debounce(async (value) => {
      setSearchDebounce(value);
    }, 500),
    []
  );

  const { data, isLoading } = useQuery(
    [infiniteStrapiUsersKeys.all, searchDebounce],
    async () => {
      const response = await restFactory.get<{ results: ReadOnlyUser[] }>(
        '/dj/api/v1/strapi_user',
        { search: searchDebounce }
      );

      return response;
    },
    {
      onError: (error: StrapiApiError) => {
        toast.error(`Error loading users. ${error || ''}`);
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const onUserClick = async (user: ReadOnlyUser) => {
    await ghostUser(user.email);
    history.push('/');
  };

  return (
    <Container className="mt-4 fix-bg-images" fluid="xl">
      <BackgroundImages showTopImage={false} />
      <h1>Users</h1>
      <Row>
        <Col xs={12} md={{ span: 6, offset: 6 }}>
          <Form.Group className="mb-0">
            <BsSearch
              className={classnames(
                'text-primary-dark font-weight-bold',
                styles.searchIcon
              )}
            />
            <Form.Control
              type="text"
              placeholder="Search for users"
              className={styles.searchInput}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setSearch(value);
                debouncedSearchUpdate(value);
              }}
              value={search}
            />
            <Form.Text className="text-muted">
              Search by first name, last name or username (email)
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <div className="pt-5">
          <Table hover responsive>
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Role</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {data?.results.map((user) => (
                <tr
                  key={user.id}
                  className="cursor-pointer"
                  onClick={() => onUserClick(user)}
                >
                  <td>{user.first_name}</td>
                  <td>{user.last_name}</td>
                  <td>{getUserRoleStr(getUserRole(user))}</td>
                  <td>{user.email}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Container>
  );
};

export default connect(null, { ghostUser })(Users);
